import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useToast,
  useDisclosure,
  FormErrorMessage,
  VStack,
  Card,
  CardBody,
  IconButton,
  InputGroup,
  InputLeftElement,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,

  Select,
  useColorModeValue
} from '@chakra-ui/react';
import {
  SearchIcon,
  AddIcon
} from '@chakra-ui/icons';
import {
  FiBookOpen,
  FiVideo,
  FiFilter,
  FiRefreshCw,
  FiPlus
} from 'react-icons/fi';
import { FaUpload } from 'react-icons/fa';
import { useDropzone } from 'react-dropzone';
import { motion } from 'framer-motion';
import { createCourse, fetchCourses } from '../services/courseService';
import CourseCard2 from '../shared/CourseCard2';
import axiosInstance from '../services/axiosInstance';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const CoursesAdminContent = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [courseName, setCourseName] = useState('');
  const [professor, setProfessor] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instructorsLoading, setInstructorsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCourses, setFilteredCourses] = useState([]);
  const toast = useToast();

  // Color scheme
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('#2C3E50', 'white');

  // State to track validation errors
  const [errors, setErrors] = useState({
    courseName: false,
    professor: false,
    thumbnail: false,
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setThumbnail(acceptedFiles[0]);
      setErrors(prev => ({ ...prev, thumbnail: false }));
    },
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  // Fetch instructors from server
  const fetchInstructors = async () => {
    try {
      setInstructorsLoading(true);
      const response = await axiosInstance.get('/users/instructors');
      const instructorUsers = response.data.filter(user => user.role === 'instructor');
      setInstructors(instructorUsers);
    } catch (error) {
      console.error('Error fetching instructors:', error);
      toast({
        title: 'خطأ في جلب قائمة الأساتذة',
        description: "تعذر استرداد قائمة الأساتذة. يرجى المحاولة مرة أخرى لاحقًا",
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setInstructorsLoading(false);
    }
  };

  // Filter courses based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = courses.filter(course =>
        course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCourses(filtered);
    } else {
      setFilteredCourses(courses);
    }
  }, [courses, searchTerm]);

  const handleSave = async () => {
    // Validate before saving
    const newErrors = {
      courseName: courseName.trim() === '',
      professor: !professor,
      thumbnail: !thumbnail,
    };

    setErrors(newErrors);

    if (newErrors.courseName || newErrors.professor || newErrors.thumbnail) {
      toast({
        title: 'خطأ في التحقق من البيانات',
        description: 'يرجى التأكد من ملء جميع الحقول المطلوبة بشكل صحيح',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const formData = new FormData();
    formData.append('name', courseName);
    formData.append('teacherName', professor.username);
    formData.append('teacherId', professor._id);
    formData.append('vignetteImage', thumbnail);

    try {
      setLoading(true);
      const response = await createCourse(formData);
      await fetchAndSetCourses();

      // Reset fields after saving
      resetForm();

      toast({
        title: 'تم إنشاء المادة بنجاح',
        description: "تمت إضافة المادة بنجاح إلى النظام",
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('خطأ في إنشاء المادة:', error);
      toast({
        title: 'خطأ في إنشاء المادة',
        description: "حدث خطأ أثناء إنشاء المادة. يرجى المحاولة مرة أخرى",
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }

    onClose();
  };

  const resetForm = () => {
    setCourseName('');
    setProfessor(null);
    setThumbnail(null);
    setErrors({
      courseName: false,
      professor: false,
      thumbnail: false,
    });
  };

  const fetchAndSetCourses = async () => {
    try {
      setLoading(true);
      const courses = await fetchCourses();
      setCourses(courses || []);
    } catch (error) {
      console.error('خطأ في جلب المواد:', error);
      toast({
        title: 'خطأ في استرداد المواد',
        description: "تعذر استرداد قائمة المواد. يرجى المحاولة مرة أخرى لاحقًا",
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndSetCourses();
    fetchInstructors();
  }, []);

  if (loading && courses.length === 0) {
    return (
      <Flex h="100vh" align="center" justify="center" dir="rtl">
        <VStack spacing={4}>
          <Spinner size="xl" color={primaryColor} thickness="4px" />
          <Text color={textColor} fontFamily="Cairo, sans-serif">جاري تحميل المواد...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box dir="rtl" fontFamily="Cairo, sans-serif">
      <Box
        flex="1"
        p={6}
        bg={bgColor}
        transition="margin 0.3s ease"
        minHeight="100vh"
      >
        {/* Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
            <CardBody p={6}>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                flexDirection={{ base: 'column', md: 'row' }}
                gap={4}
              >
                <VStack align="start" spacing={2}>
                  <Heading
                    as="h1"
                    size="xl"
                    color={secondaryColor}
                    fontFamily="Cairo, sans-serif"
                    fontWeight="700"
                  >
                    إدارة المواد التعليمية
                  </Heading>
                  <Text color="gray.600" fontSize="md">
                    إنشاء وإدارة المواد والمحتوى التعليمي
                  </Text>
                </VStack>

                <HStack spacing={4} flexDirection={{ base: 'column', sm: 'row' }} w={{ base: 'full', md: 'auto' }}>
                  <InputGroup maxW="300px">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="البحث في المواد..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      bg="gray.50"
                      border="2px solid"
                      borderColor="gray.200"
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      borderRadius="lg"
                    />
                  </InputGroup>

                  <Button
                    bg={primaryColor}
                    color={secondaryColor}
                    _hover={{ bg: '#d4b016' }}
                    _active={{ bg: '#c4a014' }}
                    borderRadius="lg"
                    fontWeight="600"
                    leftIcon={<AddIcon />}
                    onClick={onOpen}
                    boxShadow="md"
                    fontFamily="Cairo, sans-serif"
                  >
                    إضافة مادة جديدة
                  </Button>

                  <IconButton
                    icon={<FiRefreshCw />}
                    aria-label="تحديث"
                    bg="white"
                    border="2px solid"
                    borderColor="gray.200"
                    _hover={{ borderColor: primaryColor, bg: `${primaryColor}10` }}
                    borderRadius="lg"
                    onClick={fetchAndSetCourses}
                    isLoading={loading}
                  />
                </HStack>
              </Flex>
            </CardBody>
          </Card>
        </motion.div>

        {/* Statistics */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
            <motion.div variants={fadeIn}>
              <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                <CardBody p={6}>
                  <Stat>
                    <Flex align="center" justify="space-between">
                      <Box>
                        <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                          إجمالي المواد
                        </StatLabel>
                        <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                          {courses.length}
                        </StatNumber>
                        <StatHelpText color="green.500" fontSize="sm">
                          <StatArrow type="increase" />
                          نشطة ومتاحة
                        </StatHelpText>
                      </Box>
                      <Box
                        bg={`${primaryColor}20`}
                        p={3}
                        borderRadius="lg"
                      >
                        <Icon as={FiBookOpen} color={primaryColor} boxSize={6} />
                      </Box>
                    </Flex>
                  </Stat>
                </CardBody>
              </Card>
            </motion.div>

            <motion.div variants={fadeIn}>
              <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                <CardBody p={6}>
                  <Stat>
                    <Flex align="center" justify="space-between">
                      <Box>
                        <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                          المواد المفلترة
                        </StatLabel>
                        <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                          {filteredCourses.length}
                        </StatNumber>
                        <StatHelpText color="blue.500" fontSize="sm">
                          نتائج البحث الحالية
                        </StatHelpText>
                      </Box>
                      <Box
                        bg="blue.100"
                        p={3}
                        borderRadius="lg"
                      >
                        <Icon as={FiFilter} color="blue.500" boxSize={6} />
                      </Box>
                    </Flex>
                  </Stat>
                </CardBody>
              </Card>
            </motion.div>

            <motion.div variants={fadeIn}>
              <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                <CardBody p={6}>
                  <Stat>
                    <Flex align="center" justify="space-between">
                      <Box>
                        <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                          إجمالي المحاضرات
                        </StatLabel>
                        <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                          {courses.reduce((total, course) => {
                            const chapters = course.chapitres || [];
                            return total + chapters.reduce((chapterTotal, chapter) => {
                              const parts = chapter.parties || [];
                              return chapterTotal + parts.reduce((partTotal, part) => {
                                const videos = part.videos || [];
                                return partTotal + videos.length;
                              }, 0);
                            }, 0);
                          }, 0)}
                        </StatNumber>
                        <StatHelpText color="purple.500" fontSize="sm">
                          محتوى تعليمي متنوع
                        </StatHelpText>
                      </Box>
                      <Box
                        bg="purple.100"
                        p={3}
                        borderRadius="lg"
                      >
                        <Icon as={FiVideo} color="purple.500" boxSize={6} />
                      </Box>
                    </Flex>
                  </Stat>
                </CardBody>
              </Card>
            </motion.div>
          </SimpleGrid>
        </motion.div>

        {/* Courses Grid */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {filteredCourses.length === 0 ? (
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
              <CardBody p={12}>
                <VStack spacing={4}>
                  <Icon as={FiBookOpen} boxSize={12} color="gray.400" />
                  <Text fontSize="lg" fontWeight="600" color="gray.500" fontFamily="Cairo, sans-serif">
                    {searchTerm ? 'لا توجد مواد تطابق البحث' : 'لا توجد مواد متاحة'}
                  </Text>
                  <Text color="gray.400" textAlign="center" fontFamily="Cairo, sans-serif">
                    {searchTerm
                      ? 'جرب البحث بكلمات مختلفة أو امسح مربع البحث لعرض جميع المواد'
                      : 'ابدأ بإنشاء مادة جديدة لبناء المحتوى التعليمي'
                    }
                  </Text>
                  {!searchTerm && (
                    <Button
                      bg={primaryColor}
                      color={secondaryColor}
                      _hover={{ bg: '#d4b016' }}
                      borderRadius="lg"
                      fontWeight="600"
                      leftIcon={<AddIcon />}
                      onClick={onOpen}
                      mt={4}
                      fontFamily="Cairo, sans-serif"
                    >
                      إنشاء أول مادة
                    </Button>
                  )}
                </VStack>
              </CardBody>
            </Card>
          ) : (
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={6}>
              {filteredCourses.map((course, index) => (
                <motion.div key={course._id} variants={fadeIn}>
                  <CourseCard2
                    course={course}
                    refresh={fetchAndSetCourses}
                  />
                </motion.div>
              ))}
            </SimpleGrid>
          )}
        </motion.div>

        {/* Create Course Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnOverlayClick={false}>
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent borderRadius="xl" fontFamily="Cairo, sans-serif">
            <ModalHeader bg={secondaryColor} color="white" borderTopRadius="xl">
              <Text fontSize="lg" fontWeight="700">
                إنشاء مادة تعليمية جديدة
              </Text>
            </ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody p={6}>
              <VStack spacing={6} align="stretch">
                <FormControl id="courseName" isInvalid={errors.courseName}>
                  <FormLabel color={textColor} fontWeight="600" fontFamily="Cairo, sans-serif">
                    عنوان المادة
                  </FormLabel>
                  <Input
                    value={courseName}
                    onChange={(e) => {
                      setCourseName(e.target.value);
                      setErrors(prev => ({ ...prev, courseName: e.target.value.trim() === '' }));
                    }}
                    placeholder="أدخل عنوان المادة"
                    bg="gray.50"
                    border="2px solid"
                    borderColor={errors.courseName ? "red.300" : "gray.200"}
                    _focus={{
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 1px ${primaryColor}`
                    }}
                    borderRadius="lg"
                    fontFamily="Cairo, sans-serif"
                  />
                  {errors.courseName && (
                    <FormErrorMessage fontFamily="Cairo, sans-serif">
                      عنوان المادة مطلوب
                    </FormErrorMessage>
                  )}
                </FormControl>

                <FormControl id="professor" isInvalid={errors.professor}>
                  <FormLabel color={textColor} fontWeight="600" fontFamily="Cairo, sans-serif">
                    اسم الأستاذ
                  </FormLabel>
                  <Select
                    placeholder={instructorsLoading ? "جاري تحميل الأساتذة..." : "اختر الأستاذ"}
                    value={professor ? professor._id : ''}
                    onChange={(e) => {
                      const selectedInstructor = instructors.find(instructor => instructor._id === e.target.value);
                      setProfessor(selectedInstructor);
                      setErrors(prev => ({ ...prev, professor: !selectedInstructor }));
                    }}
                    bg="gray.50"
                    border="2px solid"
                    borderColor={errors.professor ? "red.300" : "gray.200"}
                    _focus={{
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 1px ${primaryColor}`
                    }}
                    borderRadius="lg"
                    fontFamily="Cairo, sans-serif"
                    isDisabled={instructorsLoading}
                  >
                    {instructors.map((instructor) => (
                      <option key={instructor._id} value={instructor._id}>
                        {instructor.username}
                      </option>
                    ))}
                  </Select>
                  {errors.professor && (
                    <FormErrorMessage fontFamily="Cairo, sans-serif">
                      اسم الأستاذ مطلوب
                    </FormErrorMessage>
                  )}
                </FormControl>

                <FormControl id="thumbnail" isInvalid={errors.thumbnail}>
                  <FormLabel color={textColor} fontWeight="600" fontFamily="Cairo, sans-serif">
                    صورة مصغرة للمادة
                  </FormLabel>
                  <Box
                    {...getRootProps()}
                    border="2px dashed"
                    borderColor={errors.thumbnail ? 'red.300' : isDragActive ? primaryColor : 'gray.300'}
                    bg={isDragActive ? `${primaryColor}10` : 'gray.50'}
                    p={8}
                    textAlign="center"
                    cursor="pointer"
                    borderRadius="lg"
                    transition="all 0.3s ease"
                    _hover={{
                      borderColor: primaryColor,
                      bg: `${primaryColor}10`
                    }}
                  >
                    <input {...getInputProps()} />
                    <VStack spacing={3}>
                      <Icon as={FaUpload} boxSize={8} color={primaryColor} />
                      {thumbnail ? (
                        <VStack spacing={2}>
                          <Text fontWeight="600" color={textColor} fontFamily="Cairo, sans-serif">
                            {thumbnail.name}
                          </Text>
                          <Text fontSize="sm" color="gray.500" fontFamily="Cairo, sans-serif">
                            حجم الملف: {(thumbnail.size / 1024 / 1024).toFixed(2)} MB
                          </Text>
                        </VStack>
                      ) : (
                        <VStack spacing={2}>
                          <Text fontWeight="600" color={textColor} fontFamily="Cairo, sans-serif">
                            {isDragActive ? 'أفلت الملف هنا' : 'اسحب وأفلت صورة أو انقر للتحميل'}
                          </Text>
                          <Text fontSize="sm" color="gray.500" fontFamily="Cairo, sans-serif">
                            يدعم: JPG, PNG, GIF, WebP (حد أقصى 5MB)
                          </Text>
                        </VStack>
                      )}
                    </VStack>
                  </Box>
                  {errors.thumbnail && (
                    <FormErrorMessage fontFamily="Cairo, sans-serif">
                      صورة مصغرة مطلوبة
                    </FormErrorMessage>
                  )}
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter bg="gray.50" borderBottomRadius="xl">
              <HStack spacing={3} w="full" justify="space-between">
                <Button
                  variant="ghost"
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                  fontFamily="Cairo, sans-serif"
                >
                  إلغاء
                </Button>

                <Button
                  bg="green.500"
                  color="white"
                  _hover={{ bg: 'green.600' }}
                  onClick={handleSave}
                  isLoading={loading}
                  loadingText="جاري الحفظ..."
                  rightIcon={<Icon as={FiPlus} />}
                  fontFamily="Cairo, sans-serif"
                >
                  إنشاء المادة
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Box >
  );
};

const CourseAdminManagement = () => {
  return <CoursesAdminContent />;
};

export default CourseAdminManagement;